import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, RotateCcw, Info, Globe, BookOpen, AlertTriangle, MapPin, Scale, FileText, ExternalLink, ChevronDown, ChevronUp, DollarSign, Receipt } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useSession } from '@/context/SessionContext';
import type { StandardCode } from '@/types/standards';
import { getStandard, ALL_STANDARDS } from '@/types/standards';
import { toast } from 'sonner';

/* ─── Extended jurisdictional details ─── */
const UK_JURISDICTIONS = [
  {
    name: 'England',
    body: 'Environment Agency (EA)',
    act: 'Environmental Permitting Regulations 2016 (EPR)',
    permit: 'Water Discharge Activity Permit',
    permitCode: 'EPR/SRxxxx',
    typicalLimits: { tss: 30, bod5: 20, cod: 100, nh3: 5, og: 10, phMin: 6.0, phMax: 9.0 },
    keyNotes: [
      'Treated effluent to controlled waters: TSS <30 mg/L, BOD <20 mg/L',
      'Trade effluent to sewer: consent via water utility under Water Industry Act 1991',
      'Groundwater discharge: EPR permit + risk assessment required',
      'Storm overflow: regulated under DWF (Dry Weather Flow) conditions',
      'Nutrient neutrality required in sensitive catchments (e.g. Solent, Wye)',
      'MCERTS-certified monitoring required for permit compliance',
    ],
    website: 'https://www.gov.uk/guidance/control-of-activity-in-the-water-environment',
  },
  {
    name: 'Wales',
    body: 'Natural Resources Wales (NRW)',
    act: 'Environmental Permitting (Wales) Regulations 2016',
    permit: 'Water Discharge Permit',
    permitCode: 'EPR/NRWxxxx',
    typicalLimits: { tss: 30, bod5: 20, cod: 100, nh3: 5, og: 10, phMin: 6.0, phMax: 9.0 },
    keyNotes: [
      'Same UWWTD-derived limits as England',
      'Water Framework Directive (WFD) status drives tightening',
      'Agricultural pollution regulations also apply to farm effluents',
      'Bathing water catchment: stricter microbiological + nutrient limits',
    ],
    website: 'https://naturalresources.wales/',
  },
  {
    name: 'Scotland',
    body: 'SEPA',
    act: 'Water Environment (Controlled Activities) Regulations 2011 (CAR)',
    permit: 'CAR Licence (General Binding Rules or Authorisation)',
    permitCode: 'CAR/SExxxx',
    typicalLimits: { tss: 30, bod5: 20, cod: 100, nh3: 5, og: 10, phMin: 6.0, phMax: 9.0 },
    keyNotes: [
      'CAR licensing: GBR (low risk) vs Authorisation (higher risk)',
      'Diffuse pollution priority catchments: additional requirements',
      'River basin management plans (RBMP) under WFD drive limits',
      'High organic loads (food processing): BOD limits often tighter',
    ],
    website: 'https://www.sepa.org.uk/',
  },
];

const AU_JURISDICTIONS = [
  {
    name: 'Victoria',
    body: 'EPA Victoria',
    act: 'Environment Protection Act 2017',
    permit: 'EPA Permission / Works Approval',
    permitCode: 'P/L-xxxxx',
    typicalLimits: { tss: 45, bod5: 250, cod: 400, nh3: 40, og: 15, phMin: 6.0, phMax: 10.0 },
    keyNotes: [
      'New general environmental duty (GED) since 2021: proactive risk management',
      'Trade waste to Barwon Water / Melbourne Water / Yarra Valley Water: TSS <60 mg/L typical',
      'Industrial wastewater to sewer: EPA permission + trade waste agreement',
      'Reuse / irrigation: AGWR Tier 1–3 classification required',
      'Groundwater recharge: EPA works approval + risk assessment',
      'Greater Melbourne: sewer acceptance standards set by water retailers',
    ],
    website: 'https://www.epa.vic.gov.au/',
  },
  {
    name: 'New South Wales',
    body: 'NSW EPA',
    act: 'Protection of the Environment Operations Act 1997',
    permit: 'Environment Protection Licence (EPL)',
    permitCode: 'EPLxxxx',
    typicalLimits: { tss: 50, bod5: 250, cod: 400, nh3: 30, og: 15, phMin: 6.0, phMax: 10.0 },
    keyNotes: [
      'POEO Act: EPL required for scheduled premises discharging >1000 L/day',
      'Sydney Water / Hunter Water trade waste: TSS <60 mg/L, O&G <15 mg/L',
      'Coal seam gas (CSG) produced water: strict salt/TDS limits',
      'Nutrient export to Great Barrier Reef catchment: additional P/N caps',
      'Load-based licensing (LBL): mass loading limits, not just concentration',
    ],
    website: 'https://www.epa.nsw.gov.au/',
  },
  {
    name: 'Queensland',
    body: 'Department of Environment & Science (DES)',
    act: 'Environmental Protection Act 1994',
    permit: 'Environmental Authority (EA)',
    permitCode: 'EA/EPxxxx',
    typicalLimits: { tss: 50, bod5: 250, cod: 400, nh3: 40, og: 15, phMin: 6.0, phMax: 9.0 },
    keyNotes: [
      'EA issued under EP Act; sewage treatment works (>1000 p.e.) require approval',
      'Water quality objectives (WQOs) by catchment under EPP (Water) 2019',
      'Great Barrier Reef catchment: Reef protection regulations (dissolved inorganic N + P)',
      'Resource sector (mining, coal seam gas): additional water quality conditions',
      'Irrigation reuse: stock water / irrigation water quality guidelines (ANZECC 2000)',
    ],
    website: 'https://environment.des.qld.gov.au/',
  },
  {
    name: 'South Australia',
    body: 'SA EPA',
    act: 'Environment Protection Act 1993',
    permit: 'Licence under EP Act',
    permitCode: 'EP/L-xxxx',
    typicalLimits: { tss: 50, bod5: 300, cod: 400, nh3: 30, og: 15, phMin: 6.0, phMax: 9.5 },
    keyNotes: [
      'South Australian Environment Protection (Water Quality) Policy 2015 (EPP Water)',
      'Trade waste to SA Water: controlled via SA Water trade waste agreement (separate from SA EPA licence)',
      'Murray-Darling Basin: additional salinity + nutrient limits apply',
      'Glenelg to Adelaide pipeline: drinking water quality standards for reuse',
      'Stormwater management: Water Sensitive Urban Design (WSUD) encouraged',
    ],
    website: 'https://www.epa.sa.gov.au/',
  },
  {
    name: 'Western Australia',
    body: 'DWER',
    act: 'Environmental Protection Act 1986',
    permit: 'Licence under EP Act',
    permitCode: 'Lxxxx',
    typicalLimits: { tss: 50, bod5: 300, cod: 400, nh3: 40, og: 15, phMin: 6.0, phMax: 9.5 },
    keyNotes: [
      'DWER licensing; Perth metro: Water Corporation trade waste agreement',
      'Mining sector: dominant; acid mine drainage (AMD) is major concern',
      'Aluminium refinery red mud: very high pH — neutralisation required',
      'Dewatering from mines: licence + discharge monitoring required',
      'Kimberley / Pilbara: remote sites — often on-site evaporation ponds',
      'Groundwater-dependent ecosystems (GDEs): additional protection triggers',
    ],
    website: 'https://www.dwer.wa.gov.au/',
  },
];

const STANDARDS_LIST = [
  {
    code: 'UK_EA' as StandardCode,
    name: 'UK Environment Agency',
    flag: '🇬🇧',
    available: true,
    description: 'Environment Agency discharge permits under EPR 2016. Covers water discharge activities, groundwater permits, and trade effluent consent.',
    body: 'EA (England) / NRW (Wales) / SEPA (Scotland)',
    act: 'EPR 2016 / Water Industry Act 1991 / UWWTD 2024/3019 (retained EU law)',
    permit: 'Water Discharge Activity Permit / Trade Effluent Consent',
    color: 'border-blue-500/30 bg-blue-500/5',
    activeColor: 'border-rb-brand/40 bg-rb-brand/5',
  },
  {
    code: 'AU_EPA' as StandardCode,
    name: 'Australian EPA',
    flag: '🇦🇺',
    available: true,
    description: 'State EPA discharge standards under NWQMS and AGWR. Covers all states with jurisdiction-specific licence conditions.',
    body: 'Vic EPA / NSW EPA / Qld DES / SA EPA / WA DWER',
    act: 'ANZG (2018) DGVs / NWQMS / State EP Acts',
    permit: 'Environment Protection Licence (EPL) / Works Approval',
    color: 'border-emerald-500/30 bg-emerald-500/5',
    activeColor: 'border-rb-brand/40 bg-rb-brand/5',
  },
  {
    code: 'SA_WATER' as StandardCode,
    name: 'SA Water — Trade Waste',
    flag: '🇦🇺',
    available: true,
    description: 'SA Water trade waste discharge conditions for connection to the SA Water sewerage network. Includes excess concentration charges (AUD/kg) when annual thresholds are exceeded.',
    body: 'SA Water (South Australian Water Corporation)',
    act: 'Water Industry Act 2004 (SA) / SA Water RWAS',
    permit: 'Trade Waste Agreement (TWA) / Permit',
    color: 'border-blue-500/30 bg-blue-500/5',
    activeColor: 'border-blue-400/50 bg-blue-500/8',
  },
  {
    code: 'PL_WP' as StandardCode,
    name: 'Poland — Wody Polskie',
    flag: '🇵🇱',
    available: true,
    description: 'Polish water-permit conditions (pozwolenie wodnoprawne) issued by Państwowe Gospodarstwo Wodne Wody Polskie (PGW WP) via its regional RZGW offices. Discharge limits per Rozporządzenie Dz.U. 2019 poz. 1311 (transposes the EU UWWTD); enforcement by the regional WIOŚ.',
    body: 'Wody Polskie (PGW WP) — RZGW; enforcement WIOŚ / GIOŚ',
    act: 'Prawo wodne (Dz.U. 2017 poz. 1566) / Rozporządzenie Dz.U. 2019 poz. 1311',
    permit: 'Pozwolenie wodnoprawne / pozwolenie zintegrowane (IPPC)',
    color: 'border-red-500/30 bg-red-500/5',
    activeColor: 'border-red-400/50 bg-red-500/8',
  },
  {
    code: 'EU_UWWTD' as StandardCode,
    name: 'EU UWWTD',
    flag: '🇪🇺',
    available: false,
    description: 'Council Directive 91/271/EEC concerning urban waste-water treatment, as amended by Directive (EU) 2024/3019.',
    body: 'European Commission / EEA / National Competent Authorities',
    act: 'Directive 91/271/EEC / WFD 2000/60/EC',
    permit: 'Urban Wastewater Treatment Permit',
    color: 'border-slate-700 bg-transparent',
    activeColor: 'border-rb-brand/40 bg-rb-brand/5',
  },
];

const LIMIT_PARAMS = [
  { key: 'tss',            name: 'TSS',          unit: 'mg/L', uk: 35,   au: 60,   sa: 500,  eu: 35,  pl: 35,   note: 'Suspended solids' },
  { key: 'bod5',           name: 'BOD₅',         unit: 'mg/L', uk: 25,   au: 300,  sa: 1000, eu: 25,  pl: 25,   note: 'Biochemical oxygen demand' },
  { key: 'cod',            name: 'COD',           unit: 'mg/L', uk: 125,  au: 500,  sa: 2000, eu: 125, pl: 125,  note: 'Chemical oxygen demand' },
  { key: 'og',             name: 'Oil & Grease',  unit: 'mg/L', uk: 15,   au: 15,   sa: 100,  eu: 15,  pl: 15,   note: 'Fats, oils, grease' },
  { key: 'tds',            name: 'TDS',           unit: 'mg/L', uk: 2000, au: 2000, sa: 1500, eu: '—', pl: 2000, note: 'Total dissolved solids' },
  { key: 'totalNitrogen',  name: 'Total N / TKN', unit: 'mg/L', uk: 10,   au: 200,  sa: 130,  eu: 10,  pl: 15,   note: 'Nitrogen (TN or TKN)' },
  { key: 'totalPhosphorus',name: 'Total P',       unit: 'mg/L', uk: 1,    au: 10,   sa: 15,   eu: 1,   pl: 2,    note: 'Phosphorus (P)' },
  { key: 'phMin',          name: 'pH min',        unit: '',     uk: 6.0,  au: 6.0,  sa: 6.0,  eu: 6.0, pl: 6.5,  note: 'Lower bound' },
  { key: 'phMax',          name: 'pH max',        unit: '',     uk: 9.0,  au: 10.0, sa: 10.0, eu: 9.0, pl: 9.0,  note: 'Upper bound' },
  { key: 'temperature',    name: 'Temperature',   unit: '°C',   uk: 30,   au: 40,   sa: 38,   eu: 30,  pl: 35,   note: 'Max at discharge point' },
  { key: 'lead',           name: 'Lead',          unit: 'mg/L', uk: 0.5,  au: 0.05, sa: 0.5,  eu: 0.5, pl: 0.5,  note: 'Heavy metal' },
  { key: 'chromium',       name: 'Chromium',      unit: 'mg/L', uk: 1.0,  au: 0.5,  sa: 1.0,  eu: 1.0, pl: 0.5,  note: 'Heavy metal' },
  { key: 'ammoniaN',       name: 'NH₃-N',         unit: 'mg/L', uk: 5,    au: 100,  sa: 100,  eu: 5,   pl: 10,   note: 'Ammonia nitrogen' },
];

const SA_WATER_CHARGES = [
  { param: 'BOD',  cond: '≤1,000 mg/L',       rate: 0.251, note: 'Per kg BOD discharged' },
  { param: 'BOD',  cond: '>1,000 mg/L',        rate: 0.378, note: 'Higher rate — high-strength' },
  { param: 'SS',   cond: '≤500 mg/L',          rate: 0.218, note: 'Per kg suspended solids' },
  { param: 'SS',   cond: '>500 mg/L',          rate: 0.315, note: 'Above acceptance limit' },
  { param: 'TDS',  cond: 'All concentrations', rate: 0.126, note: 'Per kg dissolved solids' },
  { param: 'TKN',  cond: '>650 mg/L',          rate: 0.392, note: 'Total Kjeldahl Nitrogen' },
  { param: 'TP',   cond: 'All concentrations', rate: 1.908, note: 'Total Phosphorus — highest rate' },
];

/* ─── Component ─── */
export default function StandardsPage() {
  const { activeStandard, setActiveStandard } = useSession();
  const [compareMode, setCompareMode] = useState(false);
  const [expandedUK, setExpandedUK] = useState(true);
  const [expandedAU, setExpandedAU] = useState(true);
  const [expandedSAW, setExpandedSAW] = useState(true);
  const [selectedUK, setSelectedUK] = useState(0);
  const [selectedAU, setSelectedAU] = useState(0);

  const handleSelect = (code: StandardCode) => {
    const std = STANDARDS_LIST.find(s => s.code === code);
    if (!std?.available) return;
    setActiveStandard(code);
    const newStd = getStandard(code);
    toast.success(`Standard changed to ${newStd.shortName}`, {
      description: `All discharge limits, compliance checks, and reports now use ${newStd.country} regulations.`,
    });
  };

  const activeInfo = getStandard(activeStandard);

  return (
    <div className="max-w-[1440px] mx-auto space-y-6">
      <PageHeader icon={Globe} title="Regulatory Standards"
        subtitle="Discharge standards and compliance frameworks — UK EA, AU EPA, SA Water, EU UWWTD, US EPA" />

      {/* Active Standard Banner */}
      <div className={`flex items-center gap-3 p-4 rounded-xl border ${
        activeStandard === 'SA_WATER'
          ? 'border-blue-500/30 bg-blue-500/8'
          : 'border-emerald-500/20 bg-emerald-500/5'
      }`}>
        <Globe className={`w-5 h-5 ${activeStandard === 'SA_WATER' ? 'text-blue-400' : 'text-emerald-400'}`} />
        <div className="flex-1">
          <span className={`text-xs font-bold uppercase tracking-wider ${activeStandard === 'SA_WATER' ? 'text-blue-400' : 'text-emerald-400'}`}>Currently Active</span>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg">{activeInfo.flag}</span>
            <span className="text-sm font-semibold text-white">{activeInfo.name}</span>
            <span className="text-xs text-slate-500">— all assessments and reports use these limits</span>
          </div>
        </div>
        <div className="hidden sm:block text-right">
          <p className="text-[10px] text-slate-500">{activeInfo.actReference}</p>
          <p className="text-[10px] text-slate-500">{activeInfo.permitType}</p>
        </div>
      </div>

      {/* Standard Selector Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {STANDARDS_LIST.map((std) => (
          <motion.button
            key={std.code}
            whileHover={{ scale: std.available ? 1.01 : 1 }}
            whileTap={{ scale: std.available ? 0.99 : 1 }}
            onClick={() => handleSelect(std.code)}
            className={`relative p-5 rounded-xl border text-left transition-all ${
              activeStandard === std.code
                ? std.activeColor
                : std.available
                ? `${std.color} hover:border-slate-600 bg-rb-elevated`
                : 'border-slate-800/50 bg-rb-surface opacity-50 cursor-not-allowed'
            }`}
          >
            {activeStandard === std.code && (
              <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-rb-brand/20 flex items-center justify-center">
                <Check className="w-3.5 h-3.5 text-rb-brand" />
              </div>
            )}
            {!std.available && (
              <div className="absolute top-3 right-3 text-[10px] text-slate-600 bg-slate-800 px-2 py-0.5 rounded-full">Coming Soon</div>
            )}
            {std.code === 'SA_WATER' && (
              <div className="absolute top-3 right-3 text-[9px] text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-full font-bold">
                🇦🇺 SA Only
              </div>
            )}
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{std.flag}</span>
              <div>
                <h3 className="text-sm font-semibold text-slate-200">{std.name}</h3>
                <p className="text-[10px] text-slate-500">{std.body}</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">{std.description}</p>
            <p className="text-[10px] text-slate-600 mt-2">{std.act}</p>
          </motion.button>
        ))}
      </div>

      {/* ─── UK JURISDICTIONS ─── */}
      {activeStandard === 'UK_EA' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <button onClick={() => setExpandedUK(!expandedUK)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-200">
            <span className="text-lg">🇬🇧</span> UK Jurisdictions
            {expandedUK ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedUK && (
            <>
              <div className="flex flex-wrap gap-2">
                {UK_JURISDICTIONS.map((j, i) => (
                  <button key={j.name} onClick={() => setSelectedUK(i)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedUK === i ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : 'border-slate-700 text-slate-500 hover:text-slate-300'}`}>
                    {j.name}
                  </button>
                ))}
              </div>
              <div className="p-4 space-y-3 rb-inner-card">
                {(() => { const j = UK_JURISDICTIONS[selectedUK]; return (
                  <>
                    <div className="flex items-start justify-between">
                      <div><h4 className="text-sm font-bold text-white">{j.name}</h4><p className="text-xs text-slate-400">{j.body}</p></div>
                      <a href={j.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-cyan-400 hover:underline"><ExternalLink className="w-3 h-3" /> Website</a>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
                      {[['Act', j.act],['Permit', j.permit],['Code Format', j.permitCode],['Key Limit', `BOD₅ < ${j.typicalLimits.bod5} mg/L`]].map(([label, val]) => (
                        <div key={label} className="p-2 rb-card-sm"><span className="text-slate-500">{label}</span><p className="text-slate-300 font-medium mt-0.5">{val}</p></div>
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      {j.keyNotes.map((note, i) => (
                        <div key={i} className="flex items-start gap-2 text-[10px] text-slate-400"><FileText className="w-3 h-3 mt-0.5 shrink-0 text-blue-400" /><span>{note}</span></div>
                      ))}
                    </div>
                  </>
                ); })()}
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* ─── AUSTRALIAN JURISDICTIONS ─── */}
      {activeStandard === 'AU_EPA' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <button onClick={() => setExpandedAU(!expandedAU)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-200">
            <span className="text-lg">🇦🇺</span> Australian State Jurisdictions
            {expandedAU ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedAU && (
            <>
              <div className="flex flex-wrap gap-2">
                {AU_JURISDICTIONS.map((j, i) => (
                  <button key={j.name} onClick={() => setSelectedAU(i)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedAU === i ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'border-slate-700 text-slate-500 hover:text-slate-300'}`}>
                    {j.name}
                  </button>
                ))}
              </div>
              <div className="p-4 space-y-3 rb-inner-card">
                {(() => { const j = AU_JURISDICTIONS[selectedAU]; return (
                  <>
                    <div className="flex items-start justify-between">
                      <div><h4 className="text-sm font-bold text-white">{j.name}</h4><p className="text-xs text-slate-400">{j.body}</p></div>
                      <a href={j.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-cyan-400 hover:underline"><ExternalLink className="w-3 h-3" /> Website</a>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
                      {[['Act', j.act],['Permit', j.permit],['Code Format', j.permitCode],['Key Limit', `TSS < ${j.typicalLimits.tss} mg/L`]].map(([label, val]) => (
                        <div key={label} className="p-2 rb-card-sm"><span className="text-slate-500">{label}</span><p className="text-slate-300 font-medium mt-0.5">{val}</p></div>
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      {j.keyNotes.map((note, i) => (
                        <div key={i} className="flex items-start gap-2 text-[10px] text-slate-400"><FileText className="w-3 h-3 mt-0.5 shrink-0 text-emerald-400" /><span>{note}</span></div>
                      ))}
                    </div>
                  </>
                ); })()}
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* ─── SA WATER SECTION ─── */}
      {activeStandard === 'SA_WATER' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

          {/* SA Water TWA details */}
          <button onClick={() => setExpandedSAW(!expandedSAW)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-200">
            <span className="text-lg">🇦🇺</span> SA Water — Trade Waste Agreement Details
            {expandedSAW ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {expandedSAW && (
            <div className="bg-rb-elevated border border-blue-500/20 rounded-xl p-4 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-sm font-bold text-white">SA Water (South Australian Water Corporation)</h4>
                  <p className="text-xs text-slate-400">Trade Waste to SA Water sewer network — Water Industry Act 2004 (SA)</p>
                </div>
                <a href="https://www.sawater.com.au/business-and-industry/trade-waste" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[10px] text-cyan-400 hover:underline"><ExternalLink className="w-3 h-3" /> sawater.com.au</a>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
                {[
                  ['Act', 'Water Industry Act 2004 (SA)'],
                  ['Permit', 'Trade Waste Agreement (TWA)'],
                  ['Permit Ref', 'TW100046 / TW_49 (example)'],
                  ['NATA Lab', 'AWQC (Acc. No. 1115)'],
                ].map(([label, val]) => (
                  <div key={label} className="p-2 rb-card-sm">
                    <span className="text-slate-500">{label}</span>
                    <p className="text-slate-300 font-medium mt-0.5">{val}</p>
                  </div>
                ))}
              </div>

              {/* Discharge limits table */}
              <div>
                <p className="text-[10px] font-bold text-blue-300 uppercase tracking-wider mb-2">SA Water Discharge Limits (RWAS)</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
                  {[
                    ['TSS', '≤ 500 mg/L'],['BOD₅', '≤ 3,000 mg/L (TWA-specific)'],['Oil & Grease', '≤ 100 mg/L'],
                    ['TDS', '≤ 1,500 mg/L'],['TKN', '≤ 130 mg/L N'],['Total P', '≤ 15 mg/L'],
                    ['pH', '6.0 – 10.0'],['Temperature', '≤ 38°C'],
                  ].map(([param, val]) => (
                    <div key={param} className="p-2 rounded-lg bg-blue-500/5 border border-blue-500/15">
                      <span className="text-blue-400 font-bold">{param}</span>
                      <p className="text-slate-300 mt-0.5">{val}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                {getStandard('SA_WATER').complianceNotes.map((note, i) => (
                  <div key={i} className="flex items-start gap-2 text-[10px] text-slate-400">
                    <FileText className="w-3 h-3 mt-0.5 shrink-0 text-blue-400" />
                    <span>{note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Excess Concentration Charges */}
          <div className="bg-rb-elevated border border-blue-500/20 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-500/15 bg-blue-900/15">
              <Receipt className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs font-bold text-blue-300 uppercase tracking-wider">Excess Concentration Charges</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20 font-bold">AUD excl. GST</span>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-blue-500/8 border border-blue-500/15">
                <Info className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                <p className="text-[10px] text-blue-200 leading-relaxed">
                  <strong>Charge triggers:</strong> Annual discharge exceeding <strong>10 t BOD or SS</strong>,
                  <strong> 20 t TDS</strong>, or <strong>10,000 kL</strong> volume. Once triggered, charges apply
                  per kg at the rates below. Source: SA Water Fees &amp; Charges Schedule.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="border-b border-slate-800/60">
                      <th className="text-left py-2 pr-3 text-slate-500 font-bold uppercase tracking-wider">Parameter</th>
                      <th className="text-left py-2 px-2 text-slate-500 font-bold uppercase tracking-wider">Condition</th>
                      <th className="text-right py-2 px-2 text-slate-500 font-bold uppercase tracking-wider">Rate (AUD/kg)</th>
                      <th className="text-left py-2 pl-2 text-slate-500 font-bold uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SA_WATER_CHARGES.map((ch, i) => (
                      <tr key={i} className="border-b border-slate-800/40">
                        <td className="py-2 pr-3 font-black text-blue-300">{ch.param}</td>
                        <td className="py-2 px-2 text-slate-400">{ch.cond}</td>
                        <td className={`py-2 px-2 text-right tabular-nums font-black ${ch.rate >= 1.0 ? 'text-red-400' : ch.rate >= 0.3 ? 'text-amber-400' : 'text-slate-300'}`}>
                          ${ch.rate.toFixed(3)}
                        </td>
                        <td className="py-2 pl-2 text-slate-500">{ch.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[9px] text-slate-600">
                Use the <strong>Charges Calculator</strong> in the Customer tab (Overview → SA Water Charges) to estimate annual charges based on your sample data.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* ─── LIMITS COMPARISON ─── */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
          <Scale className="w-4 h-4" /> Discharge Limits Comparison
        </h3>
        <button
          onClick={() => setCompareMode(!compareMode)}
          className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
            compareMode ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400' : 'border-slate-700 text-slate-400 hover:text-slate-300'
          }`}
        >
          {compareMode ? 'Hide Comparison' : 'Compare All Standards'}
        </button>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="overflow-hidden rb-inner-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] text-slate-500 uppercase tracking-wider border-b border-slate-800">
                <th className="px-4 py-3">Parameter</th>
                <th className="px-4 py-3">Unit</th>
                <th className={`px-4 py-3 text-right ${activeStandard === 'UK_EA' ? 'text-rb-brand' : ''}`}>
                  <span className="flex items-center justify-end gap-1">🇬🇧 UK EA {activeStandard === 'UK_EA' && '★'}</span>
                </th>
                {compareMode && (
                  <th className={`px-4 py-3 text-right ${activeStandard === 'AU_EPA' ? 'text-rb-brand' : ''}`}>
                    <span className="flex items-center justify-end gap-1">🇦🇺 AU EPA {activeStandard === 'AU_EPA' && '★'}</span>
                  </th>
                )}
                {compareMode && (
                  <th className={`px-4 py-3 text-right ${activeStandard === 'SA_WATER' ? 'text-blue-400' : ''}`}>
                    <span className="flex items-center justify-end gap-1">🇦🇺 SA Water {activeStandard === 'SA_WATER' && '★'}</span>
                  </th>
                )}
                <th className={`px-4 py-3 text-right ${activeStandard === 'PL_WP' ? 'text-red-400' : ''}`}>
                  <span className="flex items-center justify-end gap-1">🇵🇱 PL Wody Polskie {activeStandard === 'PL_WP' && '★'}</span>
                </th>
                {compareMode && (
                  <th className="px-4 py-3 text-right">
                    <span className="flex items-center justify-end gap-1">🇪🇺 EU UWWTD</span>
                  </th>
                )}
                <th className="px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {LIMIT_PARAMS.map((param) => (
                <tr key={param.key} className="hover:bg-slate-800/20 transition-colors">
                  <td className="px-4 py-3 text-sm text-slate-300 font-medium">{param.name}</td>
                  <td className="px-4 py-3 text-[10px] text-slate-500">{param.unit}</td>
                  <td className={`px-4 py-3 text-right ${activeStandard === 'UK_EA' ? 'bg-rb-brand/5' : ''}`}>
                    <span className="text-sm text-white tabular-nums font-medium">{param.uk}</span>
                  </td>
                  {compareMode && (
                    <td className={`px-4 py-3 text-right ${activeStandard === 'AU_EPA' ? 'bg-rb-brand/5' : ''}`}>
                      <span className={`text-sm tabular-nums ${param.au !== param.uk ? 'text-amber-400' : 'text-slate-400'}`}>{param.au}</span>
                      {param.au !== param.uk && <span className="text-[10px] text-slate-600 ml-1">{Number(param.au) > Number(param.uk) ? '↑' : '↓'}</span>}
                    </td>
                  )}
                  {compareMode && (
                    <td className={`px-4 py-3 text-right ${activeStandard === 'SA_WATER' ? 'bg-blue-500/5' : ''}`}>
                      <span className={`text-sm tabular-nums ${param.sa !== param.uk ? 'text-blue-400' : 'text-slate-400'}`}>{param.sa}</span>
                      {param.sa !== param.uk && <span className="text-[10px] text-slate-600 ml-1">{Number(param.sa) > Number(param.uk) ? '↑' : '↓'}</span>}
                    </td>
                  )}
                  <td className={`px-4 py-3 text-right ${activeStandard === 'PL_WP' ? 'bg-red-500/5' : ''}`}>
                    <span className={`text-sm tabular-nums ${param.pl !== param.uk ? 'text-red-400' : 'text-slate-400'}`}>{param.pl}</span>
                    {param.pl !== param.uk && param.pl !== '—' && <span className="text-[10px] text-slate-600 ml-1">{Number(param.pl) > Number(param.uk) ? '↑' : '↓'}</span>}
                  </td>
                  {compareMode && (
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-slate-400 tabular-nums">{param.eu}</span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-[10px] text-slate-500">{param.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* ─── COMPLIANCE NOTES ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activeStandard === 'UK_EA' && (
          <div className="p-4 space-y-2 rb-inner-card">
            <h4 className="text-xs font-semibold text-slate-200 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> UK Compliance Notes
            </h4>
            {getStandard('UK_EA').complianceNotes.map((note, i) => (
              <div key={i} className="flex items-start gap-2 text-[10px] text-slate-400">
                <Info className="w-3 h-3 mt-0.5 shrink-0 text-slate-600" /><span>{note}</span>
              </div>
            ))}
          </div>
        )}
        {activeStandard === 'AU_EPA' && (
          <div className="p-4 space-y-2 rb-inner-card">
            <h4 className="text-xs font-semibold text-slate-200 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-emerald-400" /> Australian Compliance Notes
            </h4>
            {getStandard('AU_EPA').complianceNotes.map((note, i) => (
              <div key={i} className="flex items-start gap-2 text-[10px] text-slate-400">
                <Info className="w-3 h-3 mt-0.5 shrink-0 text-slate-600" /><span>{note}</span>
              </div>
            ))}
          </div>
        )}
        {activeStandard === 'SA_WATER' && (
          <div className="bg-rb-elevated border border-blue-500/20 rounded-xl p-4 space-y-2">
            <h4 className="text-xs font-semibold text-slate-200 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-blue-400" /> SA Water Compliance Notes
            </h4>
            {getStandard('SA_WATER').complianceNotes.map((note, i) => (
              <div key={i} className="flex items-start gap-2 text-[10px] text-slate-400">
                <Info className="w-3 h-3 mt-0.5 shrink-0 text-blue-600" /><span>{note}</span>
              </div>
            ))}
          </div>
        )}
        {activeStandard === 'PL_WP' && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-2">
            <h4 className="text-xs font-semibold text-slate-200 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> Poland — Wody Polskie compliance notes
            </h4>
            {getStandard('PL_WP').complianceNotes.map((note, i) => (
              <div key={i} className="flex items-start gap-2 text-[10px] text-slate-400">
                <Info className="w-3 h-3 mt-0.5 shrink-0 text-red-600" /><span>{note}</span>
              </div>
            ))}
          </div>
        )}
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 space-y-2">
          <h4 className="text-xs font-semibold text-blue-300 flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> Regulatory Note
          </h4>
          <p className="text-[10px] text-slate-400 leading-relaxed">
            Limits shown are indicative values from published regulatory guidance. Actual discharge consent limits are site-specific
            and must be obtained from the relevant regulatory authority. SA Water limits are from the Restricted Wastewater
            Acceptance Standards (RWAS) and individual Trade Waste Agreements (TWA) — site permit limits may differ.
          </p>
        </div>
      </div>
    </div>
  );
}
